import { h } from 'preact';
import { useState, useMemo } from 'preact/hooks';
import parse from 'html-react-parser';
import PlusIcon from 'utils/icon/icon-plus.svg';
import MinusIcon from 'utils/icon/icon-minus.svg';

const AccordionItem = ({
  accordion,
  index,
  isLast,
  expandedAccordion,
  handleAccordionClick,
  variantContent,
  filterAccordions,
}) => {
  const isExpanded = expandedAccordion.includes(index);

  const hasContent =
    accordion.description ||
    (filterAccordions && accordion.html) ||
    ((accordion.showVariantDescription || accordion.showVariantDetails) && variantContent);

  if (!accordion.title || !hasContent) return null;

  return (
    <div
      className={`flex flex-col border-t border-grey-5 pt-20 ${isLast ? 'border-b' : ''} ${filterAccordions ? 'px-15 l:px-0' : ''}`}
    >
      <button
        className="flex flex-row items-center justify-between"
        onClick={() => handleAccordionClick(index)}
        aria-expanded={isExpanded}
      >
        <p
          className={`${filterAccordions ? 'body-16-med' : 'body-14-med'} md:body-16-med text-primary hover:underline`}
        >
          {accordion.title}
        </p>
        <div className="text-20 leading-1 text-primary">{isExpanded ? <MinusIcon /> : <PlusIcon />}</div>
      </button>
      <div className={`accordion-content ${isExpanded ? 'expanded' : 'collapsed'}`}>
        {/* Show variant content if available */}
        {variantContent}

        {/* Fallback content */}
        {!variantContent && accordion.description && (
          <div className="rte rte--small [&_ul+p]:mt-15 [&_ul]:mt-0">{parse(accordion.description)}</div>
        )}
      </div>
    </div>
  );
};

const Accordions = ({ accordions, filterAccordions = false, currentVariant = {} }) => {
  const [expandedAccordion, setExpandedAccordion] = useState([]);

  const handleAccordionClick = (index) => {
    setExpandedAccordion((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
  };

  const renderVariantDescription = useMemo(() => {
    if (!currentVariant?.description) return null;

    return (
      <div className="body-14 pb-10 text-secondary">
        <div className="rte rte--small">{parse(currentVariant.description)}</div>
      </div>
    );
  }, [currentVariant?.description, currentVariant?.masterId]);

  const renderVariantDetails = useMemo(() => {
    if (!currentVariant?.details) return null;

    return (
      <div className="body-14 pb-10 text-secondary">
        <div className="rte rte--small">{parse(currentVariant.details)}</div>
        {currentVariant?.masterId && (
          <div className="body-14-light mt-[15px]">
            {window.TMW.Strings.product.itemMasterId} {currentVariant.masterId}
          </div>
        )}
      </div>
    );
  }, [currentVariant?.details, currentVariant?.masterId]);

  return (
    <div>
      {accordions.length > 0 &&
        accordions.map((accordion, index) => {
          const isDescriptionAccordion = accordion.title.toLowerCase().includes('description');
          const isDetailsAccordion = accordion.title.toLowerCase().includes('details');

          const variantContent = isDescriptionAccordion
            ? renderVariantDescription
            : isDetailsAccordion
              ? renderVariantDetails
              : null;

          return (
            <AccordionItem
              key={`${accordion.title}-${currentVariant?.id}-${index}`}
              accordion={{
                ...accordion,
                showVariantDescription: isDescriptionAccordion,
                showVariantDetails: isDetailsAccordion,
              }}
              index={index}
              isLast={index === accordions.length - 1}
              expandedAccordion={expandedAccordion}
              handleAccordionClick={handleAccordionClick}
              variantContent={variantContent}
              filterAccordions={filterAccordions}
              currentVariant={currentVariant}
            />
          );
        })}
    </div>
  );
};

export default Accordions;
